import React from 'react'
import { ClonedComponentByShowModal, UnhappyModal } from '../modal'

export class UnhappyError extends Error {
  constructor(message: string) {
    super()
    this.message = message
  }
}

interface UnhappyModalBoundaryState {
  hasError: boolean
  message: string
}

interface UnhappyModalBoundaryProps extends ClonedComponentByShowModal {
  children?: React.ReactNode
}

/**
 * ErrorBoundary to wrap dialogs that need to verify that their main data exists,
 * before going on, mostly used to clean nullable values on our dialogs. Example:
 *
 * if (!organisation) {
 *   throw new UnhappyError("No organisation found.")
 * }
 *
 *  Afterwards we keep working but now organisation is not nullable or undefined
 *  anymore and we can use hooks since there are no condifional rendering.
 **/
export class UnhappyModalBoundary extends React.Component<UnhappyModalBoundaryProps, UnhappyModalBoundaryState> {
  state = {
    hasError: false,
    message: ''
  }

  static getDerivedStateFromError(error: Error) {
    if (error instanceof UnhappyError) {
      return { hasError: true, message: error.message }
    }

    return { hasError: false, error: null, message: '' }
  }

  componentDidCatch(error: Error, _errorInfo: React.ErrorInfo): void {
    if (!(error instanceof UnhappyError)) {
      throw error
    }
  }

  render() {
    if (this.state.hasError) {
      return <UnhappyModal title={this.state.message} onClose={this.props?.onClose} />
    }

    return this.props.children
  }
}

export const withUnhappyModalBoundary = <TProps extends { onClose?: () => void }>(
  DialogComponent: React.ComponentType<React.PropsWithChildren<TProps>>
) => {
  return function WrappedComponent(props: TProps) {
    return (
      <UnhappyModalBoundary onClose={props?.onClose}>
        <DialogComponent {...DialogComponent.defaultProps} {...props} />
      </UnhappyModalBoundary>
    )
  }
}
